// Multi-page PDF: each group becomes ONE PDF page with all its SVGs stacked in
// a configurable column grid. Each SVG has its own title placed DIRECTLY above it.
import { jsPDF } from "jspdf";
import "svg2pdf.js";

// Draws a semi-transparent diagonal watermark across the current PDF page.
function drawWatermark(doc, text) {
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  doc.saveGraphicsState();
  // Faint gray, rotated diagonally, centered.
  doc.setTextColor(180);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(64);
  // jsPDF applies GState opacity via setGState — fallback to gray color if not supported.
  if (typeof doc.setGState === "function" && typeof doc.GState === "function") {
    try {
      doc.setGState(new doc.GState({ opacity: 0.12 }));
    } catch {
      // ignore
    }
  }
  doc.text(text, pageW / 2, pageH / 2, {
    align: "center",
    angle: 30,
  });
  doc.restoreGraphicsState();
  doc.setTextColor(0);
}

export async function svgsToPdf(
  groups,
  filename = "reference-sheet.pdf",
  cols = 2,
  options = {}
) {
  const { watermark = null } = options;
  const doc = new jsPDF({
    orientation: "portrait",
    unit: "pt",
    format: "a4",
  });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 30;

  for (let g = 0; g < groups.length; g++) {
    const grp = groups[g];
    if (g > 0) doc.addPage();

    const items = grp.items || [];
    const isSingle = items.length === 1;
    const COLS = Math.max(1, cols);
    const rows = Math.ceil(items.length / COLS);

    // For multi-item groups, show the group title at the top of the page.
    // For a single-item group, we skip the page-top title entirely — the
    // per-item title (right above the image) is enough.
    const headerH = isSingle ? 0 : 40;
    if (!isSingle) {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(18);
      doc.text(grp.title, margin, margin + 15);
    }

    const availH = pageH - margin * 2 - headerH;
    const rowH = availH / Math.max(rows, 1);
    const colW = (pageW - margin * 2) / COLS;
    const parser = new DOMParser();
    const titleH = 20; // space reserved for per-image title above the SVG

    for (let i = 0; i < items.length; i++) {
      const { svgString, subtitle } = items[i];
      const col = i % COLS;
      const row = Math.floor(i / COLS);
      const cellX = margin + col * colW;
      const cellY = margin + headerH + row * rowH;

      const svgEl = parser.parseFromString(svgString, "image/svg+xml")
        .documentElement;
      const vb = (svgEl.getAttribute("viewBox") || "0 0 800 300")
        .split(/\s+/)
        .map(Number);
      const vbW = vb[2] || 800;
      const vbH = vb[3] || 300;
      const maxW = colW - 10;
      const maxH = rowH - titleH - 8;
      const scale = Math.min(maxW / vbW, maxH / vbH);
      const drawW = vbW * scale;
      const drawH = vbH * scale;

      // Per-image title — placed IMMEDIATELY ABOVE the SVG, centered on the cell.
      const label = subtitle || grp.title || "";
      doc.setFont("helvetica", "bold");
      doc.setFontSize(isSingle ? 16 : 11);
      const cellCenterX = cellX + colW / 2;
      doc.text(label, cellCenterX, cellY + titleH - 4, { align: "center" });

      // Center the SVG horizontally in its cell
      const svgX = cellX + (colW - drawW) / 2;
      await doc.svg(svgEl, {
        x: svgX,
        y: cellY + titleH,
        width: drawW,
        height: drawH,
      });
    }

    // Apply watermark AFTER content so it sits on top.
    if (watermark) {
      drawWatermark(doc, watermark);
    }
  }
  // Reliable download using a blob URL + anchor click (works cross-browser)
  const blob = doc.output("blob");
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
